/**
 * Workspace slug + shareable signup link card.
 *
 * Firms set a URL-safe slug (`acme` instead of `385b8eb4-b6ad-...`) so the
 * marketing/invite link looks clean. The card shows a one-click Copy for
 * the branded `/signup?f=<slug>` and `/login?f=<slug>` URLs. Also
 * surfaces the raw UUID fallback link when no slug is set yet.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Link2, Save, Loader2, Copy, Check, X as XIcon } from "lucide-react";

export default function WorkspaceSlugCard() {
  const { user, tenant, refreshMe } = useAuth();
  const canManage = ["owner", "admin"].includes(user?.role);
  const [slug, setSlug] = useState("");
  const [savedSlug, setSavedSlug] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null); // "login" | "signup" | null
  // Live availability — debounced probe to GET /tenant/slug/check.
  const [checking, setChecking] = useState(false);
  const [availability, setAvailability] = useState(null); // {available, reason, normalized}

  const load = useCallback(() => {
    setSlug(tenant?.slug || "");
    setSavedSlug(tenant?.slug || null);
  }, [tenant?.slug]);

  useEffect(() => { load(); }, [load]);

  // Debounced slug availability probe. Skip when the field matches the
  // saved value (nothing to check) or when the field is empty (we only
  // show the ✓/✗ pill once the admin starts typing something new).
  useEffect(() => {
    if (!canManage) return;
    const trimmed = slug.trim();
    if (!trimmed || trimmed === (savedSlug || "")) {
      setAvailability(null);
      return;
    }
    setChecking(true);
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get(`/tenant/slug/check?slug=${encodeURIComponent(trimmed)}`);
        setAvailability(data);
      } catch {
        setAvailability(null);
      } finally {
        setChecking(false);
      }
    }, 300);
    return () => { clearTimeout(handle); setChecking(false); };
  }, [slug, savedSlug, canManage]);

  const save = async () => {
    if (!canManage) return;
    setBusy(true);
    try {
      const { data } = await api.put("/tenant/slug", { slug: slug.trim() || null });
      setSavedSlug(data.slug);
      toast.success(data.slug ? `Slug saved: ${data.slug}` : "Slug cleared");
      await refreshMe?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save slug");
    } finally { setBusy(false); }
  };

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  // Prefer the clean `?f=<slug>` link; fall back to the raw tenant id so
  // firms without a slug still get a working shareable link.
  const linkParam = savedSlug ? `f=${savedSlug}` : (tenant?.id ? `tenant=${tenant.id}` : "");
  const signupLink = linkParam ? `${origin}/signup?${linkParam}` : "";
  const loginLink  = linkParam ? `${origin}/login?${linkParam}`  : "";

  const copy = async (link, which) => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(which);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast.error("Could not copy — please copy manually");
    }
  };

  return (
    <div className="ll-card-flat p-5 mb-6" data-testid="workspace-slug-card">
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] flex items-center gap-1.5">
          <Link2 size={11} /> Shareable workspace link
        </div>
        <div className="font-display font-bold text-xl mt-1">Branded signup &amp; sign-in URLs</div>
        <p className="text-xs text-[var(--ll-ink-soft)] mt-1 max-w-prose">
          Pick a short URL-safe handle so your invite emails read <code className="px-1 bg-[var(--ll-row-hover)]">/signup?f=acme</code> instead of a long UUID. Clients land in your firm&apos;s palette automatically.
        </p>
      </div>

      <div className="flex items-end gap-2 flex-wrap mb-2">
        <label className="flex-1 min-w-[180px]">
          <span className="block text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1">Slug</span>
          <div className="flex">
            <span
              className="inline-flex items-center px-2 border border-r-0 text-xs text-[var(--ll-ink-soft)] bg-[var(--ll-row-hover)]"
              style={{ borderColor: "var(--ll-border)" }}
            >
              /signup?f=
            </span>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase())}
              placeholder="acme"
              disabled={busy || !canManage}
              data-testid="slug-input"
              className="flex-1 px-3 py-2 border text-sm disabled:opacity-60"
              style={{ borderColor: "var(--ll-border)" }}
              maxLength={32}
            />
          </div>
        </label>
        <button
          type="button"
          onClick={save}
          disabled={
            busy || !canManage || slug === (savedSlug || "") ||
            (slug.trim() !== "" && availability && !availability.available)
          }
          data-testid="slug-save-btn"
          className="text-xs px-3 py-2 bg-[var(--ll-ink)] text-white hover:bg-[var(--ll-ink-hover)] inline-flex items-center gap-1.5 disabled:opacity-60"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          Save
        </button>
      </div>

      {/* Live availability indicator — only shown when the admin is typing
          something different from the saved value. */}
      <div className="min-h-[20px] mb-3 text-xs" data-testid="slug-availability">
        {checking && (
          <span className="inline-flex items-center gap-1.5 text-[var(--ll-ink-soft)]">
            <Loader2 size={11} className="animate-spin" /> Checking…
          </span>
        )}
        {!checking && availability && availability.available && (
          <span className="inline-flex items-center gap-1.5 text-[#065F46]" data-testid="slug-available">
            <Check size={12} /> {availability.reason || `Available${availability.normalized ? ` as ${availability.normalized}` : ""}`}
          </span>
        )}
        {!checking && availability && !availability.available && (
          <div className="flex items-start flex-wrap gap-2" data-testid="slug-taken">
            <span className="inline-flex items-center gap-1.5 text-[var(--ll-error)]">
              <XIcon size={12} /> {availability.reason}
            </span>
            {Array.isArray(availability.suggestions) && availability.suggestions.length > 0 && (
              <div className="inline-flex items-center flex-wrap gap-1.5" data-testid="slug-suggestions">
                <span className="text-[var(--ll-ink-soft)]">Try:</span>
                {availability.suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSlug(s)}
                    data-testid={`slug-suggestion-${s}`}
                    className="text-xs px-2 py-0.5 border hover:bg-[var(--ll-row-hover)]"
                    style={{ borderColor: "var(--ll-border)" }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2" data-testid="signup-link-row">
          <code className="flex-1 px-3 py-2 border text-xs break-all" style={{ borderColor: "var(--ll-border)", background: "var(--ll-row-hover)" }}>
            {signupLink || "— save a slug first —"}
          </code>
          <button
            type="button"
            onClick={() => copy(signupLink, "signup")}
            disabled={!signupLink}
            data-testid="copy-signup-link-btn"
            className="text-xs px-3 py-2 border hover:bg-[var(--ll-row-hover)] inline-flex items-center gap-1.5 disabled:opacity-60"
            style={{ borderColor: "var(--ll-border)" }}
          >
            {copied === "signup" ? <Check size={12} /> : <Copy size={12} />}
            {copied === "signup" ? "Copied" : "Copy signup"}
          </button>
        </div>
        <div className="flex items-center gap-2" data-testid="login-link-row">
          <code className="flex-1 px-3 py-2 border text-xs break-all" style={{ borderColor: "var(--ll-border)", background: "var(--ll-row-hover)" }}>
            {loginLink || "— save a slug first —"}
          </code>
          <button
            type="button"
            onClick={() => copy(loginLink, "login")}
            disabled={!loginLink}
            data-testid="copy-login-link-btn"
            className="text-xs px-3 py-2 border hover:bg-[var(--ll-row-hover)] inline-flex items-center gap-1.5 disabled:opacity-60"
            style={{ borderColor: "var(--ll-border)" }}
          >
            {copied === "login" ? <Check size={12} /> : <Copy size={12} />}
            {copied === "login" ? "Copied" : "Copy sign-in"}
          </button>
        </div>
      </div>
    </div>
  );
}
